import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ChevronLeft, ChevronRight, Info, Star, Shield, Gift, Globe, MessageSquare } from 'lucide-react';

const AUTO_COLLAPSE_MS = 5000;
const PORTAL_GREEN = '#22c55e';

const FloatingNav = ({ sections = [], showCapabilities = true }) => {
  const { language } = useLanguage();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(true);
  const timerRef = useRef(null);

  const capabilityPages = [
    { path: '/positive-reviews', labelEn: 'Positive Reviews', labelFr: 'Avis Positifs', icon: Star, iconColor: 'text-green-400' },
    { path: '/b-heard', labelEn: 'B-Heard', labelFr: 'B-Heard', icon: Shield, iconColor: 'text-blue-400' },
    { path: '/rr360', labelEn: 'R&R 360 — Reward & Resolve', labelFr: 'R&R 360 — Récompense & Résolution', icon: Gift, iconColor: 'text-purple-400' },
    { path: '/global-review', labelEn: 'Global Review', labelFr: 'Avis Global', icon: Globe, iconColor: 'text-cyan-400' },
    { path: '/guest-communication', labelEn: 'Guest Communication', labelFr: 'Communication Invités', icon: MessageSquare, iconColor: 'text-orange-400' },
  ];

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      setIsCollapsed(true);
    }, AUTO_COLLAPSE_MS);
  }, [clearTimer]);

  const handleInteraction = useCallback(() => {
    if (!isCollapsed) {
      startTimer();
    }
  }, [isCollapsed, startTimer]);

  const openNav = useCallback(() => {
    setIsCollapsed(false);
    startTimer();
  }, [startTimer]);

  const closeNav = useCallback(() => {
    clearTimer();
    setIsCollapsed(true);
  }, [clearTimer]);

  const toggleNav = useCallback(() => {
    if (isCollapsed) openNav();
    else closeNav();
  }, [isCollapsed, openNav, closeNav]);

  useEffect(() => {
    closeNav();
  }, [location.pathname, closeNav]);

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  useEffect(() => {
    if (sections.length === 0) return;
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId) => {
    handleInteraction();
    const section = document.getElementById(sectionId);
    if (section) {
      const headerOffset = 150;
      const elementPosition = section.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const isCapabilityActive = (path) => location.pathname === path;

  return (
    <>
      {/* Collapsed: polished info control | Expanded: close arrow */}
      <button
        onClick={toggleNav}
        className={`fixed z-40 top-1/2 -translate-y-1/2 shadow-lg transition-all duration-300 ${
          isCollapsed
            ? 'left-0 flex items-center gap-1 pl-2.5 pr-2 py-2.5 rounded-r-xl'
            : 'left-56 p-2 rounded-r-lg bg-[#1A1A1A] hover:bg-white/10 border border-white/10'
        }`}
        style={isCollapsed ? {
          backgroundColor: 'rgba(34,197,94,0.15)',
          borderTop: `1px solid rgba(34,197,94,0.3)`,
          borderRight: `1px solid rgba(34,197,94,0.3)`,
          borderBottom: `1px solid rgba(34,197,94,0.3)`,
          borderLeft: 'none',
        } : undefined}
        aria-label={isCollapsed ? 'Open navigation' : 'Close navigation'}
        data-testid="floating-nav-toggle"
      >
        {isCollapsed ? (
          <>
            <Info className="w-4 h-4" style={{ color: PORTAL_GREEN }} strokeWidth={2} />
            <ChevronRight className="w-3 h-3" style={{ color: PORTAL_GREEN, opacity: 0.7 }} strokeWidth={2} />
          </>
        ) : (
          <ChevronLeft className="w-4 h-4 text-white/70" strokeWidth={1.5} />
        )}
      </button>

      {/* Navigation Panel */}
      <nav
        onMouseMove={handleInteraction}
        onClick={handleInteraction}
        className={`fixed left-0 top-1/2 -translate-y-1/2 z-30 transition-transform duration-300 ease-in-out ${
          isCollapsed ? '-translate-x-full' : 'translate-x-0'
        }`}
        data-testid="floating-nav"
      >
        <div className="bg-[#1A1A1A]/95 backdrop-blur-md border border-white/10 rounded-r-xl shadow-xl p-4 max-h-[80vh] overflow-y-auto w-56">

          {showCapabilities && (
            <>
              <div className="text-xs font-bold uppercase tracking-wider text-wine mb-3 px-2">
                {language === 'fr' ? 'Capacités' : 'Capabilities'}
              </div>
              <ul className="space-y-1 mb-6">
                {capabilityPages.map((page) => (
                  <li key={page.path}>
                    <Link
                      to={page.path}
                      data-testid={`nav-capability-${page.path.replace('/', '')}`}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                        isCapabilityActive(page.path)
                          ? 'bg-wine/20 text-wine font-medium border-l-2 border-wine'
                          : 'text-white/60 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <page.icon className={`w-4 h-4 ${isCapabilityActive(page.path) ? 'text-wine' : page.iconColor}`} strokeWidth={1.5} />
                      <span>{language === 'fr' ? page.labelFr : page.labelEn}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}

          {sections.length > 0 && (
            <>
              <div className="text-xs font-bold uppercase tracking-wider text-white/40 mb-3 px-2 border-t border-white/10 pt-4">
                {language === 'fr' ? 'Cette Page' : 'This Page'}
              </div>
              <ul className="space-y-1">
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      data-testid={`nav-item-${section.id}`}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                        activeSection === section.id
                          ? 'bg-white/10 text-white font-medium border-l-2 border-white/50'
                          : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                      }`}
                    >
                      {language === 'fr' ? section.labelFr : section.labelEn}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </nav>
    </>
  );
};

export default FloatingNav;
